<template>
  <div class="distributor-landing tw-min-h-screen" :class="{ 'is-dark': isDarkMode, 'is-light': !isDarkMode }">
    <!-- ===== TOP NAVBAR ===== -->
    <header class="landing-header tw-sticky tw-top-0 tw-z-50 tw-backdrop-blur-md tw-transition-colors tw-duration-300">
      <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-h-16 tw-flex tw-items-center tw-justify-between">
        <!-- Brand -->
        <div class="tw-flex tw-items-center">
          <img src="../../assets/logo-principal.png" alt="Promolíder" class="tw-h-7 tw-w-auto" />
        </div>

        <!-- Distributor Name Badge -->
        <div v-if="distributor" class="landing-distributor-badge tw-hidden sm:tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-1.5 tw-transition-colors">
          <div class="tw-w-7 tw-h-7 tw-rounded-full tw-bg-[#18d600]/20 tw-flex tw-items-center tw-justify-center tw-text-[#18d600] tw-text-xs tw-font-bold tw-flex-shrink-0">
            {{ distributorInitials }}
          </div>
          <span class="landing-badge-text tw-text-sm">
            Recomendado por <strong class="landing-badge-name">{{ distributor.full_name }}</strong>
          </span>
        </div>

        <!-- Actions -->
        <div class="tw-flex tw-items-center tw-gap-3">
          <!-- Theme Switcher Button -->
          <button
            @click="toggleDarkMode"
            class="landing-theme-btn tw-w-9 tw-h-9 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-transition-all"
            :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
            id="landing-btn-theme"
          >
            <!-- Sun icon (when dark -> switch to light) -->
            <svg v-if="isDarkMode" class="tw-w-5 tw-h-5 tw-text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
            </svg>
            <!-- Moon icon (when light -> switch to dark) -->
            <svg v-else class="tw-w-5 tw-h-5 tw-text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
            </svg>
          </button>

          <router-link :to="{ name: 'Login' }" class="landing-login-link tw-text-sm tw-font-semibold tw-transition-colors">
            Iniciar Sesión
          </router-link>
          <button @click="openRegisterModal()" class="tw-bg-[#18d600] hover:tw-bg-green-600 tw-text-white tw-text-sm tw-font-bold tw-py-2 tw-px-4 tw-rounded-xl tw-transition-all tw-shadow-md hover:tw-shadow-lg">
            Registrarse
          </button>
        </div>
      </div>

      <!-- Mobile distributor badge -->
      <div v-if="distributor" class="sm:tw-hidden tw-px-4 tw-pb-2">
        <div class="landing-distributor-badge tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-1.5">
          <div class="tw-w-6 tw-h-6 tw-rounded-full tw-bg-[#18d600]/20 tw-flex tw-items-center tw-justify-center tw-text-[#18d600] tw-text-[10px] tw-font-bold tw-flex-shrink-0">
            {{ distributorInitials }}
          </div>
          <span class="landing-badge-text tw-text-xs">
            Recomendado por <strong class="landing-badge-name">{{ distributor.full_name }}</strong>
          </span>
        </div>
      </div>
    </header>

    <!-- ===== MAIN CONTENT ===== -->
    <main>
      <!-- Loading State -->
      <div v-if="isLoading" class="tw-flex tw-items-center tw-justify-center tw-py-32">
        <div class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-[#18d600]"></div>
      </div>

      <!-- Error / Not Found -->
      <div v-else-if="error" class="tw-max-w-lg tw-mx-auto tw-text-center tw-py-32 tw-px-4">
        <svg class="tw-w-16 tw-h-16 tw-mx-auto tw-text-gray-400 tw-mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg>
        <h2 class="landing-section-title tw-text-xl tw-font-bold tw-mb-2">Landing no encontrada</h2>
        <p class="landing-section-subtitle">El distribuidor no existe o no tiene una landing configurada.</p>
        <router-link :to="{ name: 'home' }" class="tw-inline-block tw-mt-6 tw-bg-[#18d600] tw-text-white tw-font-bold tw-px-6 tw-py-2 tw-rounded-xl hover:tw-bg-green-600 tw-transition-colors">
          Ir al inicio
        </router-link>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Hero Banner Slider -->
        <div class="tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-6">
          <HeroBannerSlider />
        </div>

        <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-space-y-12 tw-pb-16">

          <!-- ===== HIGHLIGHTED COURSES (Distributor's picks) ===== -->
          <section v-if="highlightedCourses.length > 0" class="highlighted-section">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
              <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-1.5 tw-h-8 tw-bg-[#18d600] tw-rounded-full"></div>
                <div>
                  <h2 class="landing-section-title tw-text-2xl tw-font-bold tw-font-outfit">
                    Recomendados por {{ distributor.full_name }}
                  </h2>
                  <p class="landing-section-subtitle tw-text-sm tw-mt-0.5">Cursos destacados especialmente para ti</p>
                </div>
              </div>

              <!-- Slider Navigation Controls (when more than 3 courses) -->
              <div v-if="highlightedCourses.length > 3" class="tw-flex tw-items-center tw-gap-2">
                <button
                  @click="scrollHighlighted(-1)"
                  class="hl-slider-arrow"
                  title="Anterior"
                  aria-label="Anterior"
                >
                  <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                </button>
                <button
                  @click="scrollHighlighted(1)"
                  class="hl-slider-arrow"
                  title="Siguiente"
                  aria-label="Siguiente"
                >
                  <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                </button>
              </div>
            </div>

            <!-- Featured Courses Slider Track -->
            <div
              class="hl-slider-track tw-flex tw-gap-5 tw-overflow-x-auto tw-pb-4"
              ref="highlightedTrack"
            >
              <div
                v-for="course in highlightedCourses"
                :key="'hl-' + course.id"
                class="highlighted-card-wrapper"
              >
                <!-- Badge superior destacado -->
                <div class="recommended-pill-badge">
                  <svg class="tw-w-3.5 tw-h-3.5 tw-text-yellow-400" viewBox="0 0 24 24" fill="#facc15">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <span>Recomendado</span>
                </div>

                <!-- Tarjeta Marketplace completa con docente, rating, precio y hover popover -->
                <Card :course="course" :categories="categories" />
              </div>
            </div>
          </section>

          <!-- ===== FREE COURSES ===== -->
          <section>
            <FreeCoursesGrid />
          </section>

          <!-- ===== SEARCH BAR ===== -->
          <div class="tw-max-w-2xl tw-mx-auto">
            <div class="tw-relative">
              <span class="tw-absolute tw-inset-y-0 tw-left-4 tw-flex tw-items-center tw-pointer-events-none">
                <svg class="tw-w-5 tw-h-5 landing-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                class="landing-search-input tw-w-full tw-py-3 tw-rounded-xl tw-transition-all"
                style="padding-left: 2.75rem; padding-right: 1rem;"
                placeholder="Busca por nombre de curso o libro..."
              />
            </div>
          </div>

          <!-- ===== FULL CATALOG ===== -->
          <section>
            <h3 class="landing-section-title tw-text-2xl tw-font-bold tw-font-outfit tw-mb-2">Catálogo Completo</h3>
            <p class="landing-section-subtitle tw-text-sm tw-mb-6">Explora todos nuestros cursos y libros.</p>

            <div v-if="filteredCatalog.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5 tw-gap-4">
              <Card
                v-for="course in filteredCatalog"
                :key="'cat-' + course.id"
                :course="course"
                :cardType="1"
                :categories="categories"
                viewMode="marketplace"
              />
            </div>
            <div v-else class="tw-text-center landing-empty-text tw-py-8">
              No se encontraron resultados para "{{ searchQuery }}".
            </div>
          </section>
        </div>
      </div>
    </main>

    <!-- ===== FOOTER ===== -->
    <FooterV />

    <!-- ===== QUICK REGISTER MODAL ===== -->
    <QuickRegisterModal
      :show="showRegisterModal"
      type="free"
      :refId="distributor ? String(distributor.id) : '1'"
      @close="showRegisterModal = false"
      @success="handleRegisterSuccess"
    />
  </div>
</template>

<script>
import HeroBannerSlider from '@/components/Student/dashboard/HeroBannerSlider';
import FreeCoursesGrid from '@/components/Student/dashboard/FreeCoursesGrid';
import Card from '@/components/courses/cards/marketplace.vue';
import FooterV from '@/components/Footer/FooterV.vue';
import QuickRegisterModal from '@/components/QuickRegisterModal.vue';

export default {
  name: 'DistributorLanding',
  components: {
    HeroBannerSlider,
    FreeCoursesGrid,
    Card,
    FooterV,
    QuickRegisterModal
  },
  data() {
    return {
      isDarkMode: false,
      isLoading: true,
      error: false,
      distributor: null,
      highlightedCourses: [],
      catalogCourses: [],
      categories: [],
      searchQuery: '',
      showRegisterModal: false,
    };
  },
  computed: {
    distributorInitials() {
      if (!this.distributor) return '';
      const n = (this.distributor.name || '').charAt(0).toUpperCase();
      const l = (this.distributor.last_name || '').charAt(0).toUpperCase();
      return n + l;
    },
    filteredCatalog() {
      if (!this.searchQuery.trim()) return this.catalogCourses;
      const term = this.searchQuery.toLowerCase();
      return this.catalogCourses.filter(c =>
        (c.title && c.title.toLowerCase().includes(term)) ||
        (c.description && c.description.toLowerCase().includes(term))
      );
    }
  },
  created() {
    this.initDarkMode();
    if (this.$route.params.username) {
      this.fetchLandingData();
    } else {
      this.error = true;
      this.isLoading = false;
    }
  },
  methods: {
    // ======= THEME MANAGEMENT =======
    initDarkMode() {
      const saved = localStorage.getItem("app_theme");
      if (saved) {
        this.isDarkMode = saved === "dark";
      } else {
        this.isDarkMode = document.documentElement.classList.contains("dark-mode") ||
          document.documentElement.classList.contains("dark") ||
          (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
      }
      this.applyTheme();
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem("app_theme", this.isDarkMode ? "dark" : "light");
      this.applyTheme();
    },
    applyTheme() {
      if (this.isDarkMode) {
        document.documentElement.classList.add("dark-mode", "dark", "tw-dark");
        document.body.classList.add("dark-mode", "dark", "tw-dark");
      } else {
        document.documentElement.classList.remove("dark-mode", "dark", "tw-dark");
        document.body.classList.remove("dark-mode", "dark", "tw-dark");
      }
    },

    // ======= DATA FETCHING =======
    async fetchLandingData() {
      try {
        const username = this.$route.params.username;

        // 1. Fetch distributor info + highlighted courses
        const landingRes = await this.axios.get(`public/landing/${username}`);
        if (landingRes.data && landingRes.data.data) {
          this.distributor = landingRes.data.data.distributor;
          this.highlightedCourses = landingRes.data.data.highlighted_courses || [];

          // Save referral info in localStorage
          localStorage.setItem('ref_distributor_id', this.distributor.id);
          localStorage.setItem('ref_distributor_username', this.distributor.username);
        }

        // 2. Fetch full catalog (same endpoint as guest dashboard)
        const catalogRes = await this.axios.get('course/related-courses');
        if (catalogRes.data && Array.isArray(catalogRes.data.data)) {
          this.catalogCourses = catalogRes.data.data;
        }

        // 3. Fetch categories for Card component
        try {
          const catRes = await this.axios.get('category/list');
          this.categories = catRes.data.data || [];
        } catch (e) {
          this.categories = [];
        }

      } catch (err) {
        console.error('Error loading distributor landing:', err);
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },
    scrollHighlighted(direction) {
      const track = this.$refs.highlightedTrack;
      if (!track) return;
      const scrollAmount = 260 * direction;
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    },
    openRegisterModal() {
      this.pendingCourse = null;
      this.showRegisterModal = true;
    },
    handleRegisterSuccess() {
      this.showRegisterModal = false;
      if (this.pendingCourse) {
        const course = this.pendingCourse;
        const slug = course.slug || course.title.toLowerCase().replace(/\s+/g, '-');
        const routeName = course.type === 'book' ? 'buy-books' : 'buy-cursos';
        this.$router.push({ name: routeName, params: { ide: course.id, slug } });
      } else {
        // Redirect to home/dashboard
        this.$router.push({ name: 'home' });
      }
    },
  }
};
</script>

<style scoped>
/* ====================================================
   BASE TRANSITIONS
   ==================================================== */
.distributor-landing {
  transition: background-color 0.3s ease, color 0.3s ease;
}

.landing-header {
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* ====================================================
   FEATURED SLIDER BASE
   ==================================================== */
.hl-slider-track {
  scrollbar-width: thin;
  scrollbar-color: rgba(24, 214, 0, 0.4) transparent;
}
.hl-slider-track::-webkit-scrollbar {
  height: 6px;
}
.hl-slider-track::-webkit-scrollbar-thumb {
  background: rgba(24, 214, 0, 0.4);
  border-radius: 999px;
}

.featured-course-card {
  width: 320px;
  max-width: 85vw;
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.featured-badge {
  background: linear-gradient(135deg, #18d600, #15803d);
  color: #ffffff;
}

.featured-type-badge {
  background-color: rgba(15, 23, 42, 0.75);
  color: #e2e8f0;
  backdrop-filter: blur(4px);
}

.featured-play-overlay {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(3px);
}

.featured-play-circle {
  background: #18d600;
}

/* ====================================================
   LIGHT THEME
   ==================================================== */
.distributor-landing.is-light {
  background-color: #f1f5f9;
  color: #1e293b;
}

.distributor-landing.is-light .landing-header {
  background-color: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.distributor-landing.is-light .landing-distributor-badge {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.distributor-landing.is-light .landing-badge-text {
  color: #64748b;
}

.distributor-landing.is-light .landing-badge-name {
  color: #0f172a;
}

.distributor-landing.is-light .landing-theme-btn {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
}
.distributor-landing.is-light .landing-theme-btn:hover {
  background-color: #e2e8f0;
  color: #18d600;
}

.distributor-landing.is-light .landing-login-link {
  color: #475569;
}
.distributor-landing.is-light .landing-login-link:hover {
  color: #18d600;
}

.distributor-landing.is-light .landing-section-title {
  color: #0f172a;
}

.distributor-landing.is-light .landing-section-subtitle {
  color: #64748b;
}

.distributor-landing.is-light .highlighted-section {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 1.25rem;
  padding: 1.5rem;
}

.distributor-landing.is-light .highlighted-card-wrapper {
  background-color: #ffffff;
  border: 1.5px solid rgba(24, 214, 0, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  border-radius: 14px;
  padding: 10px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}
.distributor-landing.is-light .highlighted-card-wrapper:hover {
  border-color: #18d600;
  box-shadow: 0 10px 24px rgba(24, 214, 0, 0.18);
  transform: translateY(-3px);
}

.distributor-landing.is-light .recommended-pill-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 3px 10px;
  margin-bottom: 8px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: linear-gradient(135deg, rgba(24, 214, 0, 0.15), rgba(250, 204, 21, 0.18));
  color: #15803d;
  border: 1px solid rgba(24, 214, 0, 0.35);
}

.distributor-landing.is-light .hl-slider-arrow {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  cursor: pointer;
}
.distributor-landing.is-light .hl-slider-arrow:hover {
  background-color: #18d600;
  color: #ffffff;
  border-color: #18d600;
}

.distributor-landing.is-light .landing-search-input {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #0f172a;
}
.distributor-landing.is-light .landing-search-input::placeholder {
  color: #94a3b8;
}
.distributor-landing.is-light .landing-search-input:focus {
  border-color: #18d600;
  box-shadow: 0 0 0 3px rgba(24, 214, 0, 0.15);
  outline: none;
}
.distributor-landing.is-light .landing-search-icon {
  color: #94a3b8;
}

.distributor-landing.is-light .landing-empty-text {
  color: #64748b;
}

/* ====================================================
   DARK THEME
   ==================================================== */
.distributor-landing.is-dark {
  background-color: #0a0f18;
  color: #f1f5f9;
}

.distributor-landing.is-dark .landing-header {
  background-color: rgba(10, 15, 24, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.distributor-landing.is-dark .landing-distributor-badge {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.distributor-landing.is-dark .landing-badge-text {
  color: #94a3b8;
}

.distributor-landing.is-dark .landing-badge-name {
  color: #f8fafc;
}

.distributor-landing.is-dark .landing-theme-btn {
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}
.distributor-landing.is-dark .landing-theme-btn:hover {
  background-color: rgba(255, 255, 255, 0.12);
  color: #eab308;
}

.distributor-landing.is-dark .landing-login-link {
  color: #cbd5e1;
}
.distributor-landing.is-dark .landing-login-link:hover {
  color: #18d600;
}

.distributor-landing.is-dark .landing-section-title {
  color: #f8fafc;
}

.distributor-landing.is-dark .landing-section-subtitle {
  color: #94a3b8;
}

.distributor-landing.is-dark .highlighted-section {
  background-color: rgba(24, 214, 0, 0.03);
  border: 1px solid rgba(24, 214, 0, 0.22);
  border-radius: 1.25rem;
  padding: 1.5rem;
}

.distributor-landing.is-dark .highlighted-card-wrapper {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1.5px solid rgba(24, 214, 0, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  border-radius: 14px;
  padding: 10px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}
.distributor-landing.is-dark .highlighted-card-wrapper:hover {
  border-color: #18d600;
  box-shadow: 0 0 24px rgba(24, 214, 0, 0.25);
  transform: translateY(-3px);
}

.distributor-landing.is-dark .recommended-pill-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 3px 10px;
  margin-bottom: 8px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: linear-gradient(135deg, rgba(24, 214, 0, 0.25), rgba(250, 204, 21, 0.2));
  color: #4ade80;
  border: 1px solid rgba(24, 214, 0, 0.45);
}

.distributor-landing.is-dark .hl-slider-arrow {
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  cursor: pointer;
}
.distributor-landing.is-dark .hl-slider-arrow:hover {
  background-color: #18d600;
  color: #ffffff;
  border-color: #18d600;
}

.distributor-landing.is-dark .landing-search-input {
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #f8fafc;
}
.distributor-landing.is-dark .landing-search-input::placeholder {
  color: #64748b;
}
.distributor-landing.is-dark .landing-search-input:focus {
  border-color: #18d600;
  box-shadow: 0 0 0 3px rgba(24, 214, 0, 0.25);
  outline: none;
}
.distributor-landing.is-dark .landing-search-icon {
  color: #64748b;
}

.distributor-landing.is-dark .landing-empty-text {
  color: #64748b;
}
</style>
